import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Award, Calendar, ChevronRight } from 'lucide-react';
import { useFullscreen } from '@/contexts/FullscreenContext';

/**
 * JourneyProgressPreview - ÉCRAN 9 : Aperçu Suivi des Progrès
 * Montre le suivi quotidien : graphique semaine, streak, badges
 * C'est le dernier écran avant JourneyFinal - Le fade-out de l'audio ambient démarre ici
 */
const JourneyProgressPreview = () => {
  const navigate = useNavigate();
  const { setIsFullscreen } = useFullscreen();
  const [showContent, setShowContent] = useState(false);
  const [barHeights, setBarHeights] = useState([0, 0, 0, 0, 0, 0, 0]);
  const [streakCount, setStreakCount] = useState(0);
  
  const isEnglish = localStorage.getItem('i18nextLng')?.includes('en') ||
                    localStorage.getItem('hearen_lang') === 'en';

  // Données factices pour la semaine
  const weekData = [
    { day: isEnglish ? 'Mon' : 'Lun', minutes: 10 },
    { day: isEnglish ? 'Tue' : 'Mar', minutes: 15 },
    { day: isEnglish ? 'Wed' : 'Mer', minutes: 20 },
    { day: isEnglish ? 'Thu' : 'Jeu', minutes: 18 },
    { day: isEnglish ? 'Fri' : 'Ven', minutes: 22 },
    { day: isEnglish ? 'Sat' : 'Sam', minutes: 25 },
    { day: isEnglish ? 'Sun' : 'Dim', minutes: 30 },
  ];

  const badges = [
    {
      id: 'explorer',
      name: isEnglish ? 'Calm Explorer' : 'Explorateur du calme',
      icon: '🌟',
    },
    {
      id: '7days',
      name: isEnglish ? '7 Days of Listening' : '7 jours d\'écoute',
      icon: '📅',
    },
    {
      id: '30min',
      name: isEnglish ? '30 Minutes in a Day' : '30 minutes en une journée',
      icon: '⏱️',
    },
  ];

  useEffect(() => {
    setIsFullscreen(true);
    
    // Fade-in progressif
    setTimeout(() => setShowContent(true), 300);
    
    // Animation séquentielle des barres (chaque barre monte avec un délai)
    weekData.forEach((data, index) => {
      setTimeout(() => {
        setBarHeights(prev => {
          const newHeights = [...prev];
          newHeights[index] = data.minutes;
          return newHeights;
        });
      }, 800 + (index * 100)); // Délai échelonné
    });
    
    // Animation du compteur de streak (0 → 5)
    let currentCount = 0;
    const streakInterval = setInterval(() => {
      currentCount++;
      setStreakCount(currentCount);
      if (currentCount >= 5) {
        clearInterval(streakInterval);
      }
    }, 200); // Compte de 0 à 5 en 1 seconde
    
    return () => {
      clearInterval(streakInterval);
      setIsFullscreen(false);
    };
  }, [setIsFullscreen]);

  // Fonction de fade-out avec Promise - progression exponentielle pour fin en douceur
  const fadeOutAudio = (audio, duration = 4000) => {
    if (!audio || audio.paused) {
      console.log('🎵 No audio to fade out');
      return Promise.resolve();
    }

    console.log(`🎵 Starting fade-out (${duration}ms)...`);
    
    const steps = 60; // Plus d'étapes pour plus de fluidité
    const stepTime = duration / steps;
    const startVolume = audio.volume ?? 1;

    return new Promise(resolve => {
      let currentStep = 0;

      const interval = setInterval(() => {
        currentStep += 1;
        
        // Fade-out exponentiel pour une fin en douceur (pas linéaire)
        // Formule : volume = startVolume * (1 - progress)^2
        const progress = currentStep / steps;
        const factor = Math.pow(1 - progress, 2);
        audio.volume = Math.max(0, startVolume * factor);
        
        if (currentStep % 10 === 0 || currentStep === steps) {
          console.log(`🎵 Fade-out step ${currentStep}/${steps}: volume = ${audio.volume.toFixed(4)}`);
        }

        if (currentStep >= steps || audio.volume <= 0.001) {
          clearInterval(interval);
          audio.volume = 0; // Forcer à 0 exactement
          audio.pause();
          audio.currentTime = 0;
          audio.src = '';
          console.log('🎵 Fade-out complete (volume = 0), audio stopped');
          resolve();
        }
      }, stepTime);
    });
  };

  const handleComplete = () => {
    console.log('🎵 JourneyProgressPreview: Navigation immédiate + fade-out en background...');
    
    // Lancer le fade-out en arrière-plan (SANS await) pour ne pas bloquer la navigation
    if (window.journeyAmbientAudio) {
      fadeOutAudio(window.journeyAmbientAudio, 4000).then(() => {
        // Nettoyer la référence audio après le fade-out
        window.journeyAmbientAudio = null;
        console.log('🎵 JourneyProgressPreview: Fade-out complete (volume = 0) en background');
      });
    }
    
    // Navigation IMMÉDIATE - pas d'attente du fade-out
    console.log('🎵 JourneyProgressPreview: Navigating immediately to /journey-final');
    navigate('/journey-final');
  };

  const maxMinutes = Math.max(...weekData.map(d => d.minutes));

  return (
    <div className="min-h-screen bg-[#0A0F1C] pb-32 relative overflow-hidden flex flex-col items-center justify-center px-6">
      {/* Background gradient */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(34, 197, 94, 0.3) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* Contenu principal - Fade-in + Translation */}
      <div
        className="relative z-10 max-w-md w-full transition-all duration-700 ease-out"
        style={{
          opacity: showContent ? 1 : 0,
          transform: showContent ? 'translateY(0)' : 'translateY(30px)',
        }}
      >
        {/* Bandeau pédagogique premium */}
        <div 
          className="mb-8 px-6 py-4 rounded-2xl"
          style={{
            background: 'rgba(34, 197, 94, 0.15)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
          }}
        >
          <div className="flex items-start gap-3">
            <TrendingUp className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-white/90 text-sm leading-relaxed">
                {isEnglish
                  ? 'Every day, Hearen counts your listening moments and supports your progress.'
                  : "Chaque jour, Hearen compte vos moments d'écoute et accompagne vos progrès."
                }
              </p>
            </div>
          </div>
        </div>

        {/* Carte 1 - Graphique barres (7 jours) */}
        <div
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-6 mb-6"
          style={{
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          }}
        >
          <div className="mb-4">
            <h3 className="text-white font-semibold text-lg mb-1">
              {isEnglish ? 'Listening Time This Week' : 'Temps d\'écoute cette semaine'}
            </h3>
            <p className="text-white/60 text-xs">
              {isEnglish ? 'Your listening moments' : 'Vos moments d\'écoute'}
            </p>
          </div>

          {/* Graphique en barres */}
          <div className="flex items-end justify-between gap-2 h-32 mb-3">
            {weekData.map((data, index) => (
              <div key={data.day} className="flex flex-col items-center flex-1">
                <div className="w-full flex items-end justify-center mb-2" style={{ height: '100px' }}>
                  <div
                    className="w-full rounded-t transition-all duration-500"
                    style={{
                      height: `${(barHeights[index] / maxMinutes) * 100}px`,
                      background: 'linear-gradient(to top, rgba(34, 197, 94, 0.8), rgba(34, 197, 94, 0.4))',
                      boxShadow: barHeights[index] > 0 ? '0 0 10px rgba(34, 197, 94, 0.4)' : 'none',
                      transitionDelay: `${index * 100}ms`,
                    }}
                  />
                </div>
                <span className="text-white/50 text-xs">{data.day}</span>
              </div>
            ))}
          </div>

          {/* Légende */}
          <div className="text-center text-white/60 text-xs">
            {isEnglish ? 'Weekly progress' : 'Progression hebdomadaire'}
          </div>
        </div>

        {/* Carte 2 - Streak/Régularité */}
        <div
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-6 mb-6"
          style={{
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          }}
        >
          <div className="flex items-center gap-6">
            {/* Cercle de streak */}
            <div className="relative">
              <svg width="80" height="80" viewBox="0 0 80 80">
                {/* Cercle de fond */}
                <circle
                  cx="40"
                  cy="40"
                  r="35"
                  fill="none"
                  stroke="rgba(255, 255, 255, 0.1)"
                  strokeWidth="6"
                />
                {/* Cercle de progression */}
                <circle
                  cx="40"
                  cy="40"
                  r="35"
                  fill="none"
                  stroke="rgba(34, 197, 94, 0.8)"
                  strokeWidth="6"
                  strokeDasharray={`${(streakCount / 7) * 220} 220`}
                  strokeLinecap="round"
                  transform="rotate(-90 40 40)"
                  style={{
                    transition: 'stroke-dasharray 0.5s ease-out',
                    filter: 'drop-shadow(0 0 8px rgba(34, 197, 94, 0.6))',
                  }}
                />
              </svg>
              {/* Nombre au centre */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-3xl font-bold text-green-400">
                  {streakCount}
                </span>
              </div>
            </div>

            {/* Texte */}
            <div className="flex-1">
              <h3 className="text-white font-semibold text-lg mb-1">
                {isEnglish ? 'Consistency' : 'Régularité'}
              </h3>
              <p className="text-white/70 text-sm">
                {isEnglish 
                  ? `${streakCount} consecutive days of listening`
                  : `${streakCount} jours consécutifs d'écoute`
                }
              </p>
              <div className="flex items-center gap-1 mt-2">
                <Calendar className="w-4 h-4 text-green-400" />
                <span className="text-green-400 text-xs font-semibold">
                  {isEnglish ? 'Keep going!' : 'Continuez !'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Carte 3 - Aperçu Badges */}
        <div
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-6 mb-6"
          style={{
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          }}
        >
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-5 h-5 text-yellow-400" />
            <h3 className="text-white font-semibold text-base">
              {isEnglish ? 'Rewards to Discover' : 'Récompenses à découvrir'}
            </h3>
          </div>

          <div className="space-y-3">
            {badges.map((badge) => (
              <div
                key={badge.id}
                className="flex items-center gap-3 px-4 py-3 rounded-xl opacity-60"
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <span className="text-2xl grayscale">{badge.icon}</span>
                <span className="text-white/70 text-sm">{badge.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Badge Preview */}
        <div className="text-center text-white/50 text-sm mb-6">
          {isEnglish ? 'Preview Mode' : 'Mode Aperçu'}
        </div>

        {/* CTA Terminer la visite */}
        <div className="flex justify-center">
          <button
            onClick={handleComplete}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-lg transition-all hover:scale-105 active:scale-95 text-white"
            style={{
              background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
              boxShadow: '0 8px 30px rgba(34, 197, 94, 0.4)',
            }}
          >
            {isEnglish ? 'Complete the tour' : 'Terminer la visite'}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default JourneyProgressPreview;
